import { Quaternion, Vector3 } from "three";

export type FaceLetter = "U" | "D" | "F" | "B" | "L" | "R";

export interface Move {
  face: FaceLetter;
  prime?: boolean;
  double?: boolean;
}

export interface Cubelet {
  id: number;
  position: [number, number, number];
  quaternion: [number, number, number, number];
}

export interface CubeState {
  cubelets: Cubelet[];
  history: Move[];
  scrambleMoves: Move[];
}

export const FACE_AXIS: Record<
  FaceLetter,
  { axis: "x" | "y" | "z"; layer: -1 | 1; sign: -1 | 1 }
> = {
  // For each face, sign is the rotation direction needed for a clockwise turn
  // when viewed face-on. -1 means a -90deg rotation around the named axis.
  R: { axis: "x", layer: 1, sign: -1 },
  L: { axis: "x", layer: -1, sign: 1 },
  U: { axis: "y", layer: 1, sign: -1 },
  D: { axis: "y", layer: -1, sign: 1 },
  F: { axis: "z", layer: 1, sign: -1 },
  B: { axis: "z", layer: -1, sign: 1 },
};

export function axisIndex(axis: "x" | "y" | "z"): 0 | 1 | 2 {
  return axis === "x" ? 0 : axis === "y" ? 1 : 2;
}

export function axisVec(axis: "x" | "y" | "z"): Vector3 {
  if (axis === "x") return new Vector3(1, 0, 0);
  if (axis === "y") return new Vector3(0, 1, 0);
  return new Vector3(0, 0, 1);
}

export function moveAngleRad(move: Move): number {
  const { sign } = FACE_AXIS[move.face];
  if (move.double) return sign * Math.PI; // 180 either way is the same
  return sign * (move.prime ? -1 : 1) * (Math.PI / 2);
}

export function moveDegrees(move: Move): number {
  return (moveAngleRad(move) * 180) / Math.PI;
}

export function initialState(): CubeState {
  const cubelets: Cubelet[] = [];
  let id = 0;
  for (let x = -1; x <= 1; x++) {
    for (let y = -1; y <= 1; y++) {
      for (let z = -1; z <= 1; z++) {
        cubelets.push({
          id: id++,
          position: [x, y, z],
          quaternion: [0, 0, 0, 1],
        });
      }
    }
  }
  return { cubelets, history: [], scrambleMoves: [] };
}

export function originalPositionFromId(id: number): [number, number, number] {
  return [
    Math.floor(id / 9) - 1,
    (Math.floor(id / 3) % 3) - 1,
    (id % 3) - 1,
  ];
}

export function cubeletInLayer(c: Cubelet, move: Move): boolean {
  const { axis, layer } = FACE_AXIS[move.face];
  return c.position[axisIndex(axis)] === layer;
}

export function applyMove(state: CubeState, move: Move): CubeState {
  const { axis } = FACE_AXIS[move.face];
  const angle = moveAngleRad(move);
  const rot = new Quaternion().setFromAxisAngle(axisVec(axis), angle);

  const newCubelets = state.cubelets.map((c) => {
    if (!cubeletInLayer(c, move)) return c;
    // Rotate position around origin
    const v = new Vector3(...c.position).applyQuaternion(rot);
    const newPos: [number, number, number] = [
      Math.round(v.x),
      Math.round(v.y),
      Math.round(v.z),
    ];
    // Compose rotations: world rotation applied first, then existing
    const cubeQ = new Quaternion(...c.quaternion);
    const newQ = rot.clone().multiply(cubeQ);
    const quaternion: [number, number, number, number] = [
      newQ.x,
      newQ.y,
      newQ.z,
      newQ.w,
    ];
    return {
      ...c,
      position: newPos,
      quaternion,
    };
  });

  return {
    ...state,
    cubelets: newCubelets,
    history: [...state.history, move],
  };
}

export function move(face: FaceLetter, modifier?: "prime" | "double"): Move {
  return {
    face,
    prime: modifier === "prime",
    double: modifier === "double",
  };
}

export function moveToString(m: Move): string {
  return m.face + (m.double ? "2" : m.prime ? "'" : "");
}

export function parseMove(s: string): Move | null {
  const f = s[0]?.toUpperCase();
  if (!"UDFBLR".includes(f)) return null;
  const tail = s.slice(1);
  return {
    face: f as FaceLetter,
    prime: tail === "'",
    double: tail === "2",
  };
}

export function inverseMove(m: Move): Move {
  if (m.double) return m;
  return { ...m, prime: !m.prime };
}

const FACES: FaceLetter[] = ["R", "L", "U", "D", "F", "B"];
const OPPOSITE: Record<FaceLetter, FaceLetter> = {
  R: "L", L: "R", U: "D", D: "U", F: "B", B: "F",
};

/** Random scramble. Avoids consecutive same-face turns and triple-axis
 * sequences like R L R, which are wasteful. */
export function generateScramble(length = 25): Move[] {
  const result: Move[] = [];
  let lastFace: FaceLetter | null = null;
  let lastLastFace: FaceLetter | null = null;
  while (result.length < length) {
    const f = FACES[Math.floor(Math.random() * FACES.length)];
    if (f === lastFace) continue;
    if (lastFace && OPPOSITE[f] === lastFace && lastLastFace === f) continue;
    const r = Math.random();
    const modifier = r < 0.33 ? "prime" : r < 0.66 ? "double" : undefined;
    result.push(move(f, modifier as "prime" | "double" | undefined));
    lastLastFace = lastFace;
    lastFace = f;
  }
  return result;
}

/** Reverses the recorded scramble. Simplest possible "solve". The cube must
 * be in a state reached only by the scramble (no manual moves after). */
export function reverseScramble(state: CubeState): Move[] {
  return [...state.scrambleMoves].reverse().map(inverseMove);
}

/** True iff the cube is solved (every cubelet at its original position
 * with no rotation). */
export function isSolved(state: CubeState): boolean {
  return state.cubelets.every((c) => {
    const orig = originalPositionFromId(c.id);
    if (
      c.position[0] !== orig[0] ||
      c.position[1] !== orig[1] ||
      c.position[2] !== orig[2]
    ) return false;
    // identity quaternion (allowing tiny float fuzz)
    const q = c.quaternion;
    const eps = 1e-6;
    return Math.abs(q[3] - 1) < eps && Math.abs(q[0]) < eps && Math.abs(q[1]) < eps && Math.abs(q[2]) < eps;
  });
}
